import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { listProducts } from "../../api/products";
import { listProductTypes } from "../../api/productTypes";

const ROLLUP_TYPE_CODES = ["ACCOMMODATION", "CATERING", "STAFF", "TUITION"];

// Build the customer-facing bullets for the bundled accommodation, catering,
// staffing and tuition costs. Nothing is priced per bullet.
function buildBundleInclusions(quote, locationName, productById, typeById) {
  const slots = quote.daySlots || [];
  const codeFor = (productId) => {
    const p = productById[productId];
    return p ? typeById[p.productTypeId]?.code || null : null;
  };
  const lines = [];
  const nights = Number(quote.nights) || 0;

  if (nights > 0) {
    const where = locationName ? ` at ${locationName}` : "";
    lines.push(`${nights} Night${nights === 1 ? "" : "s"} full board${where}`);
  }

  // Group TUITION slots per product so we can say "4 3-hour English lessons …"
  const tuitionCounts = {};
  for (const s of slots) {
    if (codeFor(s.productId) !== "TUITION") continue;
    tuitionCounts[s.productId] = (tuitionCounts[s.productId] || 0) + 1;
  }
  for (const productId of Object.keys(tuitionCounts)) {
    const count = tuitionCounts[productId];
    const product = productById[productId];
    let hoursPerSlot = null;
    try {
      const params = product?.derivationParams ? JSON.parse(product.derivationParams) : {};
      hoursPerSlot = params.hours_per_slot;
    } catch {
      hoursPerSlot = null;
    }
    const name = product?.name || "lessons";
    lines.push(hoursPerSlot
      ? `${count * hoursPerSlot} hours of ${name} with qualified teachers (${count} × ${hoursPerSlot} hours)`
      : `${count} ${name} with qualified teachers`);
  }

  let pmActivityCount = 0;
  for (const s of slots) {
    if (s.slot === "PM" && codeFor(s.productId) === "ACTIVITY") pmActivityCount += 1;
  }
  if (pmActivityCount > 0) {
    lines.push(`${pmActivityCount} Afternoon ${pmActivityCount === 1 ? "activity" : "activities"}`);
  }

  // Count evening cells when the grid has them; older quotes without an
  // evening row keep the one-per-night wording.
  const eveningCount = slots.filter((s) => s.slot === "EVE" && (s.productId || s.notes)).length;
  const eveningActivities = eveningCount > 0 ? eveningCount : nights;
  if (eveningActivities > 0) {
    lines.push(`${eveningActivities} Evening ${eveningActivities === 1 ? "activity" : "activities"}`);
  }

  const studentPax = Number(quote.studentPax) || 0;
  if (studentPax > 0) {
    lines.push(`${studentPax} Bliss goodie bag${studentPax === 1 ? "" : "s"}`);
  }

  return lines;
}

const brandNavy = [26, 31, 113];
const brandPink = [230, 0, 126];
const lightGrey = [245, 245, 245];

function drawBrandAccentText(pdf, text, xCenter, y, fontSize) {
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(fontSize);
  const words = text.split(" ");
  const parts = [];
  words.forEach((word, i) => {
    if (!word) return;
    if (word.length >= 2) {
      parts.push({ text: word.slice(0, 2), color: brandPink });
      if (word.length > 2) parts.push({ text: word.slice(2), color: brandNavy });
    } else {
      parts.push({ text: word, color: brandPink });
    }
    if (i < words.length - 1) parts.push({ text: " ", color: brandNavy });
  });
  const total = parts.reduce((s, p) => s + pdf.getTextWidth(p.text), 0);
  let x = xCenter - total / 2;
  parts.forEach((p) => {
    pdf.setTextColor(...p.color);
    pdf.text(p.text, x, y);
    x += pdf.getTextWidth(p.text);
  });
}

function drawLabel(pdf, label, value, x, y) {
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(...brandNavy);
  pdf.text(label + ":", x, y);
  const w = pdf.getTextWidth(label + ":") + 4;
  pdf.setTextColor(40);
  pdf.text(String(value || ""), x + w, y);
}

function drawLabelRight(pdf, label, value, rightX, y) {
  pdf.setFont("helvetica", "bold");
  const lbl = label + ":";
  const val = String(value || "");
  const lblWidth = pdf.getTextWidth(lbl) + 4;
  const startX = rightX - (lblWidth + pdf.getTextWidth(val));
  pdf.setTextColor(...brandNavy);
  pdf.text(lbl, startX, y);
  pdf.setTextColor(40);
  pdf.text(val, startX + lblWidth, y);
}

function drawSectionHeading(pdf, text, xCenter, y) {
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(...brandNavy);
  pdf.text(text, xCenter, y, { align: "center" });
}

function fmtWholePounds(n) {
  const num = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(num)) return "";
  return `£${Math.round(num).toLocaleString("en-GB")}`;
}

// Written out in full so a UK and an EU reader see the same date.
function fmtDate(d) {
  return d ? dayjs(d).format("D MMMM YYYY") : "";
}

// Fill down the left column, then the right, so the list reads in order.
function twoColumns(items) {
  const half = Math.ceil(items.length / 2);
  return items.slice(0, half).map((item, i) => [item, items[half + i] || ""]);
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function buildProgramme(quote, productById, arrivalDayOfWeek) {
  const slots = (quote.daySlots || []).slice().sort((a, b) => {
    if (a.dayIndex !== b.dayIndex) return a.dayIndex - b.dayIndex;
    const order = { AM: 0, PM: 1, FULL_DAY: 2, EVE: 3 };
    return (order[a.slot] ?? 99) - (order[b.slot] ?? 99);
  });
  const days = Math.max(0, (quote.nights || 0) + 1);
  const rows = [];
  for (let i = 1; i <= days; i++) {
    const daySlots = slots.filter((s) => s.dayIndex === i);
    const fullDay = daySlots.find((s) => s.slot === "FULL_DAY");
    const am = daySlots.find((s) => s.slot === "AM");
    const pm = daySlots.find((s) => s.slot === "PM");
    const eve = daySlots.find((s) => s.slot === "EVE");
    const cellText = (s) => {
      if (!s) return "";
      const name = s.productId ? (productById[s.productId]?.name || `#${s.productId}`) : "";
      return [name, s.notes || ""].filter(Boolean).join(" — ");
    };
    // Match the day grid: prefer real startDate; otherwise use the placeholder DOW.
    let datePart = "";
    if (quote.startDate) {
      datePart = ` · ${dayjs(quote.startDate).add(i - 1, "day").format("ddd D MMM")}`;
    } else if (arrivalDayOfWeek != null && arrivalDayOfWeek >= 0) {
      datePart = ` · ${DOW_SHORT[(arrivalDayOfWeek + i - 1) % 7]}`;
    }
    let label = `Day ${i}`;
    if (i === 1) label += " (Arrival)";
    else if (i === days) label += " (Departure)";
    label += datePart;
    // A full day spans the morning and afternoon columns, as it does in the day grid.
    rows.push(fullDay
      ? [label, { content: cellText(fullDay), colSpan: 2, styles: { halign: "center" } }, cellText(eve)]
      : [label, cellText(am), cellText(pm), cellText(eve)]);
  }
  return rows;
}

export async function renderQuotePdf(quote, { locations = [], seasons = [], arrivalDayOfWeek = null } = {}) {
  // Fetch active products + types to resolve daySlot productIds → names and categorize lines
  let productById = {};
  let typeById = {};
  try {
    const products = await listProducts({ activeOnly: false });
    products.forEach((p) => { productById[p.id] = p; });
  } catch {
    // Fall back to numeric id rendering; PDF still works
  }
  try {
    const types = await listProductTypes();
    types.forEach((t) => { typeById[t.id] = t; });
  } catch {
    // Without types, every catalogue line falls through to the individual-line branch
  }
  const codeFor = (productId) => {
    const p = productById[productId];
    return p ? typeById[p.productTypeId]?.code || null : null;
  };

  const locationName = (locations.find((l) => l.id === quote.locationId) || {}).name || "";
  const seasonName = (seasons.find((s) => s.id === quote.seasonId) || {}).name || "";
  const isOffSeason = /^off/i.test(seasonName);
  const ref = quote.quoteNumber || `BQ-${quote.id}`;
  const contactName = quote.createdByName || "";
  // The creator's staff-record email, not their sign-in name.
  const contactEmail = quote.createdByEmail || "info@brownleeschools.com";

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  pdf.setProperties({ title: `Bliss quote ${ref}${quote.clientName ? ` for ${quote.clientName}` : ""}` });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let y = 40;
  const logo = process.env.PUBLIC_URL + "/assets/img/blissLogo.png";
  const footerImg = process.env.PUBLIC_URL + "/assets/img/footer.png";

  const tableDefaults = {
    theme: "grid",
    // Bottom margin leaves room for the footer drawn on every page.
    margin: { left: 40, right: 40, bottom: 90 },
    styles: { fontSize: 10, cellPadding: 6, textColor: 40, lineColor: [180, 180, 180], lineWidth: 0.5 },
    headStyles: { fillColor: brandNavy, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: lightGrey },
  };
  const bulletStyles = {
    theme: "plain",
    alternateRowStyles: { fillColor: [255, 255, 255] },
    styles: { fontSize: 10, textColor: 40, cellPadding: { top: 2, bottom: 2, left: 4, right: 8 } },
  };
  // A heading and its table move to the next page together.
  const section = (title, minHeight) => {
    if (y + minHeight > pageHeight - 90) {
      pdf.addPage();
      y = 50;
    }
    drawSectionHeading(pdf, title, pageWidth / 2, y);
    y += 8;
  };

  // Header
  try { pdf.addImage(logo, "PNG", 40, y, 50, 60); } catch {}
  drawBrandAccentText(pdf, "Quote", pageWidth - 80, y + 38, 20);

  const lineY = y + 70;
  pdf.setDrawColor(...brandNavy);
  pdf.setLineWidth(1);
  pdf.line(40, lineY, pageWidth - 40, lineY);
  y = lineY + 18;

  pdf.setFontSize(11);
  drawLabel(pdf, "Quote ref", ref, 40, y);
  drawLabelRight(pdf, "Date", fmtDate(quote.createdAt), pageWidth - 40, y);
  y += 20;

  drawLabel(pdf, "Client", quote.clientName || "", 40, y);
  if (quote.validityUntil) {
    drawLabelRight(pdf, "Valid until", fmtDate(quote.validityUntil), pageWidth - 40, y);
  }
  y += 20;

  if (quote.clientEmail) {
    drawLabel(pdf, "Email", quote.clientEmail, 40, y);
    y += 20;
  }
  if (quote.clientAddress) {
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(40);
    const addrLines = pdf.splitTextToSize(quote.clientAddress, pageWidth - 80);
    pdf.text(addrLines, 40, y);
    y += addrLines.length * 14;
  }
  drawLabel(pdf, "Your contact", [contactName, contactEmail].filter(Boolean).join(", "), 40, y);
  y += 20;

  // Trip overview
  const studentPax = Number(quote.studentPax) || 0;
  const leaderPax = Number(quote.visitingAdultPax) || 0;
  const staffPax = Number(quote.blissStaffPax) || 0;
  y += 8;
  drawLabel(pdf, "Location", locationName, 40, y);
  drawLabelRight(pdf, "Students", String(studentPax), pageWidth - 40, y);
  y += 18;
  drawLabel(pdf, "Nights", String(quote.nights ?? ""), 40, y);
  drawLabelRight(pdf, "Group leaders", String(leaderPax), pageWidth - 40, y);
  y += 18;
  if (seasonName) drawLabel(pdf, "Season", isOffSeason ? "Off-season" : seasonName, 40, y);
  if (staffPax > 0) drawLabelRight(pdf, "Residential staff", String(staffPax), pageWidth - 40, y);
  if (seasonName || staffPax > 0) y += 18;
  y += 6;

  // Price up front, so it stays on page 1 however long the programme runs.
  section("Your price", 70);
  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    body: [
      ["Price per student", fmtWholePounds(quote.pricePerStudent)],
      [`Total for ${plural(studentPax, "student")}`, fmtWholePounds(quote.totalPrice)],
    ],
    styles: { ...tableDefaults.styles, fontSize: 12 },
    columnStyles: {
      0: { cellWidth: 340, fontStyle: "bold" },
      1: { cellWidth: "auto", halign: "right", fontStyle: "bold", textColor: brandNavy },
    },
  });
  y = pdf.lastAutoTable.finalY + 18;

  // Programme summary
  const slotWidth = (pageWidth - 80 - 110) / 3; // page less 40pt margins and the Day column
  section("Programme", 80);
  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [["Day", "Morning", "Afternoon", "Evening"]],
    body: buildProgramme(quote, productById, arrivalDayOfWeek),
    // Equal slot columns, so a long full-day name can't squeeze the others.
    columnStyles: {
      0: { cellWidth: 110, fontStyle: "bold" },
      1: { cellWidth: slotWidth },
      2: { cellWidth: slotWidth },
      3: { cellWidth: slotWidth },
    },
  });
  y = pdf.lastAutoTable.finalY + 18;

  // What's included: the bundle's inclusions, then each priced extra by name.
  // The client sees one price, not a price per line.
  const sortedLines = (quote.lines || []).slice().sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  let hasRollup = false;
  let hasTripEntry = false;
  const extras = [];
  for (const l of sortedLines) {
    const totalPrice = Number(l.totalPrice) || 0;
    const code = l.isAdHoc ? null : codeFor(l.productId);
    if (code && ROLLUP_TYPE_CODES.includes(code)) {
      hasRollup = true;
      continue;
    }
    if (!l.description) continue;
    // A free activity with no cost behind it is just part of the programme. One
    // we pay for but give away (a weekend excursion) is still listed as included.
    const hasCost = Number(l.unitCost) > 0 || Number(l.tripCost) > 0;
    if ((code === "ACTIVITY" || code === "TRIP") && totalPrice === 0 && !hasCost) continue;
    // Transfers are a private coach, not the bus pass. A trip says what its
    // costs cover: a per-head cost is entry, a per-trip cost is the coach.
    if (/transfer/i.test(l.description)) {
      extras.push(`${l.description} (private transport)`);
    } else if (code === "TRIP") {
      const entry = Number(l.unitCost) > 0;
      const transport = Number(l.tripCost) > 0;
      const covered = [entry && "entry", transport && "transport"].filter(Boolean).join(" and ");
      extras.push(covered ? `${l.description} (${covered} included)` : l.description);
      if (entry) hasTripEntry = true;
    } else extras.push(l.description);
  }
  const included = [
    ...(hasRollup ? buildBundleInclusions(quote, locationName, productById, typeById) : []),
    ...extras,
  ];
  if (included.length) {
    section("What's included", 60);
    autoTable(pdf, {
      ...tableDefaults,
      startY: y,
      body: twoColumns(included.map((l) => `•  ${l}`)),
      ...bulletStyles,
    });
    y = pdf.lastAutoTable.finalY + 18;
  }

  const notes = [];
  if (leaderPax > 0) notes.push(`The price includes ${plural(leaderPax, "group leader")} at no extra cost.`);
  if (staffPax > 0) notes.push(`${plural(staffPax, "residential Bliss staff member")} ${staffPax === 1 ? "stays" : "stay"} with the group overnight, alongside our daytime teachers and activity leaders.`);
  if (hasTripEntry) {
    notes.push(`Entrance fees for the trips listed are included for students${leaderPax > 0 ? " and group leaders" : ""}.`);
  }
  if (isOffSeason) {
    notes.push("Off-season prices apply to any dates outside the Easter and summer holidays, so the dates are flexible and agreed when you book.");
  }
  // Leave out anything the quote itself includes (we sometimes add insurance or flights).
  const anyLine = (re) => sortedLines.some((l) => re.test(l.description || ""));
  const notIncluded = [
    !anyLine(/flight|airfare/i) && "travel to and from the UK",
    !anyLine(/insurance/i) && "travel insurance",
    "personal spending money",
  ].filter(Boolean);
  if (notIncluded.length) {
    const list = notIncluded.length > 1
      ? `${notIncluded.slice(0, -1).join(", ")} and ${notIncluded[notIncluded.length - 1]}`
      : notIncluded[0];
    notes.push(`Not included: ${list}.`);
  }
  notes.push(quote.validityUntil
    ? `This quote is valid until ${fmtDate(quote.validityUntil)}.`
    : "This quote is valid for 30 days from the date above.");
  notes.push(`To book, or to change anything in the programme, contact ${contactName || "us"} at ${contactEmail}.`);
  section("Good to know", 60);
  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    body: notes.map((n) => [`•  ${n}`]),
    ...bulletStyles,
  });

  // Footer and page number on every page
  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    pdf.setPage(p);
    pdf.setDrawColor(...brandNavy);
    pdf.setLineWidth(1);
    pdf.line(40, pageHeight - 70, pageWidth - 40, pageHeight - 70);
    try { pdf.addImage(footerImg, "PNG", (pageWidth - 120) / 2, pageHeight - 55, 120, 35); } catch {}
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(120);
    pdf.text(`Page ${p} of ${pages}`, pageWidth - 40, pageHeight - 30, { align: "right" });
  }

  const safeName = (quote.clientName || "client").replace(/\s+/g, "_");
  pdf.save(`Bliss_Quote_${ref}_${safeName}.pdf`);
}
